/* eslint-disable @typescript-eslint/no-explicit-any --
   Settings page handles many dynamic form shapes and API responses. */
import { useState, useEffect, lazy, Suspense } from 'react'
import { GlowCard } from '../components/ui/GlowCard'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useBrandingStore } from '../store/branding'
import { api } from '../lib/api'
import UniFiStagingQueue from '../components/settings/UniFiStagingQueue'
import { useToast } from '../components/ui/Toast'
import { Textarea } from '../components/ui/Textarea'
import { Checkbox } from '../components/ui/Checkbox'
import { UNIFI_DEFAULT_CLOUD_URL } from '@shared/constants'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'

const LaborRulesPage = lazy(() => import('./LaborRules'))
const ProductKitsPage = lazy(() => import('./ProductKits'))
const AreaTemplatesPage = lazy(() => import('./AreaTemplates'))
const VmsLicensingPage = lazy(() => import('./VmsLicensing'))
const BrandingProfilesPage = lazy(() => import('./BrandingProfiles'))
const ProvisioningProfilesPage = lazy(() => import('./ProvisioningProfiles'))
const ImportPortalPage = lazy(() => import('./ImportPortal'))
const PermissionsPage = lazy(() => import('../components/settings/PermissionsMatrix'))
const VultrIntegrationSection = lazy(() => import('../components/settings/VultrIntegration'))
const DWSpectrumSection = lazy(() => import('../components/settings/DWSpectrumIntegration'))
const ScreenConnectSection = lazy(() => import('../components/settings/ScreenConnectIntegration'))
const NotificationSettingsPage = lazy(() => import('./NotificationSettings'))
const NotificationHistoryPage = lazy(() => import('./NotificationHistory'))

const SETTINGS_NAV = [
  { group: 'ACCOUNT', items: [
    { id: 'profile', label: 'Profile' },
    { id: 'password', label: 'Password' },
    { id: 'team', label: 'Team Members' },
  ]},
  { group: 'COMPANY', items: [
    { id: 'branding', label: 'Branding' },
  ]},
  { group: 'PRICING', items: [
    { id: 'labor-rules', label: 'Labor Rules' },
    { id: 'margins', label: 'Pricing Alerts' },
  ]},
  { group: 'PRODUCTS', items: [
    { id: 'kits', label: 'Product Kits' },
    { id: 'templates', label: 'Area Templates' },
    { id: 'licensing', label: 'VMS Licensing' },
  ]},
  { group: 'ALERTS', items: [
    { id: 'notifications', label: 'Notifications' },
    { id: 'notification-history', label: 'Notification History' },
  ]},
  { group: 'INTEGRATIONS', items: [
    { id: 'unifi', label: 'UniFi Cloud' },
    { id: 'vultr', label: 'Vultr Cloud' },
    { id: 'dw-spectrum', label: 'DW Spectrum' },
    { id: 'screenconnect', label: 'ConnectWise Control' },
    { id: 'quickbooks', label: 'QuickBooks' },
    { id: 'device-icons', label: 'Device Icons' },
  ]},
  { group: 'AI', items: [
    { id: 'ai-config', label: 'AI Configuration' },
  ]},
  { group: 'DATA', items: [
    { id: 'import', label: 'Import' },
    { id: 'export', label: 'Export' },
  ]},
  { group: 'SECURITY', items: [
    { id: 'permissions', label: 'Permissions' },
  ]},
  { group: 'ADVANCED', items: [
    { id: 'provisioning', label: 'Provisioning Profiles' },
    { id: 'labels', label: 'Device Labels' },
  ]},
] as const

type SettingsSection = typeof SETTINGS_NAV[number]['items'][number]['id']

// Map URL sub-paths to settings section IDs (every section gets a URL)
const PATH_TO_SECTION: Partial<Record<string, SettingsSection>> = {
  'profile': 'profile',
  'password': 'password',
  'team': 'team',
  'branding': 'branding',
  'labor-rules': 'labor-rules',
  'margins': 'margins',
  'kits': 'kits',
  'templates': 'templates',
  'licensing': 'licensing',
  'unifi': 'unifi',
  'quickbooks': 'quickbooks',
  'device-icons': 'device-icons',
  'ai-config': 'ai-config',
  'import': 'import',
  'export': 'export',
  'permissions': 'permissions',
  'provisioning': 'provisioning',
  'labels': 'labels',
  'branding-profiles': 'branding',
  'notifications': 'notifications',
  'notification-history': 'notification-history',
}

export default function Settings() {
  const { user } = useAuthStore()
  const { marginAlertThreshold, marginHighlightEnabled, marginProposalWarningEnabled, marginBlockBelowZero, loadBranding } = useBrandingStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Derive section from URL path if on a settings sub-route
  const subPath = location.pathname.replace(/^\/settings\/?/, '')
  const initialSection = (subPath && PATH_TO_SECTION[subPath]) || 'profile'
  const [section, setSection] = useState<SettingsSection>(initialSection)

  // Sync section when URL changes
  useEffect(() => {
    const sp = location.pathname.replace(/^\/settings\/?/, '')
    const mapped = sp ? PATH_TO_SECTION[sp] : undefined
    if (mapped && mapped !== section) setSection(mapped)
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleNavClick(id: SettingsSection) {
    setSection(id)
    // Navigate to /settings/:section for URL persistence
    navigate(`/settings/${id}`, { replace: true })
  }

  // Profile form
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name ?? '',
    email: user?.email ?? '',
    phone: '',
  })
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileMsg, setProfileMsg] = useState('')

  // Password form
  const [passwordForm, setPasswordForm] = useState({ current: '', password: '', confirm: '' })
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState('')
  const [passwordError, setPasswordError] = useState('')

  // Margin form
  const [marginForm, setMarginForm] = useState({
    threshold: marginAlertThreshold,
    highlight: marginHighlightEnabled,
    proposalWarning: marginProposalWarningEnabled,
    blockBelowZero: marginBlockBelowZero,
  })
  const [savingMargin, setSavingMargin] = useState(false)
  const [marginMsg, setMarginMsg] = useState('')

  async function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSavingProfile(true)
    setProfileMsg('')
    try {
      await api.patch(`/users/${user?.slug}`, {
        full_name: profileForm.full_name,
        email: profileForm.email,
        phone: profileForm.phone || undefined,
      })
      setProfileMsg('Profile updated.')
    } catch { setProfileMsg('Failed to update profile.') }
    finally { setSavingProfile(false) }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPasswordError('')
    setPasswordMsg('')
    if (passwordForm.password !== passwordForm.confirm) { setPasswordError('Passwords do not match.'); return }
    if (passwordForm.password.length < 8) { setPasswordError('Password must be at least 8 characters.'); return }
    setSavingPassword(true)
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.password,
      })
      setPasswordMsg('Password changed successfully.')
      setPasswordForm({ current: '', password: '', confirm: '' })
    } catch { setPasswordError('Failed to change password. Check your current password.') }
    finally { setSavingPassword(false) }
  }

  async function handleMarginSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSavingMargin(true)
    setMarginMsg('')
    try {
      await api.patch('/branding/margin-settings', {
        margin_alert_threshold: marginForm.threshold,
        margin_highlight_enabled: marginForm.highlight,
        margin_proposal_warning_enabled: marginForm.proposalWarning,
        margin_block_below_zero: marginForm.blockBelowZero,
      })
      setMarginMsg('Margin settings updated.')
      await loadBranding()
    } catch { setMarginMsg('Failed to update margin settings.') }
    finally { setSavingMargin(false) }
  }

  function renderContent() {
    const fallback = <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>

    switch (section) {
      case 'labor-rules':
        return <Suspense fallback={fallback}><LaborRulesPage /></Suspense>
      case 'kits':
        return <Suspense fallback={fallback}><ProductKitsPage /></Suspense>
      case 'templates':
        return <Suspense fallback={fallback}><AreaTemplatesPage /></Suspense>
      case 'licensing':
        return <Suspense fallback={fallback}><VmsLicensingPage /></Suspense>
      case 'branding':
        return <Suspense fallback={fallback}><BrandingProfilesPage /></Suspense>
      case 'provisioning':
        return <Suspense fallback={fallback}><ProvisioningProfilesPage /></Suspense>
      case 'import':
        return <Suspense fallback={fallback}><ImportPortalPage /></Suspense>
      case 'permissions':
        return <Suspense fallback={fallback}><PermissionsPage /></Suspense>
      case 'profile':
        return (
          <GlowCard style={{ padding: 20 }} delay={0}>
            <h3 style={cardTitleStyle}>Profile</h3>
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
              Role: <span style={{ textTransform: 'capitalize' }}>{user?.role?.replace(/_/g, ' ')}</span>
            </p>
            <form onSubmit={handleProfileSubmit} className="space-y-3">
              <div>
                <label className="v3-field-label" style={{ fontSize: 12 }}>Full Name</label>
                <input value={profileForm.full_name} onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })} className="v3-input" required />
              </div>
              <div>
                <label className="v3-field-label" style={{ fontSize: 12 }}>Email</label>
                <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} className="v3-input" required />
              </div>
              <div>
                <label className="v3-field-label" style={{ fontSize: 12 }}>Phone</label>
                <input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} className="v3-input" placeholder="(555) 123-4567" />
              </div>
              {profileMsg && <p style={{ fontSize: 12, color: 'var(--success)' }}>{profileMsg}</p>}
              <Button type="submit" loading={savingProfile}>
                Save Profile
              </Button>
            </form>
          </GlowCard>
        )

      case 'password':
        return (
          <GlowCard style={{ padding: 20 }} delay={0}>
            <h3 style={cardTitleStyle}>Password</h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <div>
                <label className="v3-field-label" style={{ fontSize: 12 }}>Current Password</label>
                <input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} className="v3-input" required />
              </div>
              <div>
                <label className="v3-field-label" style={{ fontSize: 12 }}>New Password</label>
                <input type="password" value={passwordForm.password} onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })} className="v3-input" required minLength={8} />
              </div>
              <div>
                <label className="v3-field-label" style={{ fontSize: 12 }}>Confirm New Password</label>
                <input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} className="v3-input" required />
              </div>
              {passwordError && <p style={{ fontSize: 12, color: 'var(--danger)' }}>{passwordError}</p>}
              {passwordMsg && <p style={{ fontSize: 12, color: 'var(--success)' }}>{passwordMsg}</p>}
              <Button type="submit" loading={savingPassword}>
                Change Password
              </Button>
            </form>
          </GlowCard>
        )

      case 'team':
        return (
          <GlowCard style={{ padding: 20 }} delay={0}>
            <h3 style={cardTitleStyle}>Team Members</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Team management coming soon.</p>
          </GlowCard>
        )

      case 'margins':
        return (
          <GlowCard style={{ padding: 20 }} delay={0}>
            <h3 style={cardTitleStyle}>Pricing Alerts</h3>
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
              Items below this margin will be flagged in proposals.
            </p>
            <form onSubmit={handleMarginSubmit} className="space-y-3">
              <div className="v3-field">
                <label className="v3-field-label">Minimum margin threshold (%)</label>
                <div className="v3-input-wrap" style={{ maxWidth: 120 }}>
                  <input type="number" min="0" max="100" step="1" value={marginForm.threshold}
                    onChange={(e) => setMarginForm({ ...marginForm, threshold: parseFloat(e.target.value) || 0 })}
                    className="v3-input" />
                </div>
              </div>
              <Checkbox
                label="Highlight items below threshold"
                checked={marginForm.highlight}
                onChange={(e) => setMarginForm({ ...marginForm, highlight: e.target.checked })}
              />
              <Checkbox
                label="Show warning when proposal overall margin is below threshold"
                checked={marginForm.proposalWarning}
                onChange={(e) => setMarginForm({ ...marginForm, proposalWarning: e.target.checked })}
              />
              <Checkbox
                label="Block submission if any item is below 0% (sold at a loss)"
                checked={marginForm.blockBelowZero}
                onChange={(e) => setMarginForm({ ...marginForm, blockBelowZero: e.target.checked })}
              />
              {marginMsg && <p style={{ fontSize: 12, color: marginMsg.includes('Failed') ? 'var(--danger)' : 'var(--success)' }}>{marginMsg}</p>}
              <Button type="submit" loading={savingMargin}>
                Save
              </Button>
            </form>
          </GlowCard>
        )

      case 'labels':
        return <DeviceLabelsSection />

      case 'export':
        return <DataExportSection />

      case 'unifi':
        return <UniFiIntegrationSection />

      case 'vultr':
        return <Suspense fallback={<div style={{ padding: 24, color: 'var(--text-3)' }}>Loading...</div>}><VultrIntegrationSection /></Suspense>

      case 'dw-spectrum':
        return <Suspense fallback={<div style={{ padding: 24, color: 'var(--text-3)' }}>Loading...</div>}><DWSpectrumSection /></Suspense>

      case 'screenconnect':
        return <Suspense fallback={<div style={{ padding: 24, color: 'var(--text-3)' }}>Loading...</div>}><ScreenConnectSection /></Suspense>

      case 'notifications':
        return <Suspense fallback={fallback}><NotificationSettingsPage /></Suspense>

      case 'notification-history':
        return <Suspense fallback={fallback}><NotificationHistoryPage /></Suspense>

      case 'quickbooks':
        return <QuickBooksSection />

      case 'device-icons':
        return <DeviceIconsSection />

      case 'ai-config':
        return <AIConfigSection />

      default:
        return null
    }
  }

  return (
    <div className="page-enter">
      <style>{CSS}</style>

      {/* Two-column layout: grouped nav + content */}
      <div style={{ display: 'flex', gap: 0 }}>
        {/* Mobile nav dropdown */}
        <div className="settings-mobile-nav">
          <Select
            options={SETTINGS_NAV.flatMap(g => g.items.map(i => ({ value: i.id, label: i.label })))}
            value={section}
            onChange={(e) => handleNavClick(e.target.value as SettingsSection)}
          />
        </div>

        {/* Grouped sidebar nav */}
        <div className="settings-nav">
          {SETTINGS_NAV.map(group => (
            <div key={group.group} style={{ marginBottom: 12 }}>
              <div className="settings-nav-group">{group.group}</div>
              {group.items.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`settings-nav-item ${section === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Content area */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 16, maxWidth: ['unifi', 'labor-rules', 'kits', 'templates', 'licensing', 'branding', 'provisioning', 'import', 'permissions', 'notifications', 'notification-history'].includes(section) ? undefined : 560 }}>
          <div className="page-enter" key={section}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

function DeviceLabelsSection() {
  const [format, setFormat] = useState('{{brand}}-{{model}}-{{index}}')
  const [prefix, setPrefix] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  const preview = format
    .replace('{{brand}}', 'Axis')
    .replace('{{model}}', 'P3245')
    .replace('{{index}}', '01')
    .replace('{{location}}', 'Lobby')
    .replace('{{floor}}', '1F')

  return (
    <GlowCard style={{ padding: 20 }} delay={0}>
      <h3 style={cardTitleStyle}>Device Labels</h3>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
        Configure automatic label formatting for new devices.
      </p>
      <div className="space-y-3">
        <div>
          <label className="v3-field-label" style={{ fontSize: 12 }}>Label Format Template</label>
          <input value={format} onChange={e => setFormat(e.target.value)} className="v3-input" placeholder="{{brand}}-{{model}}-{{index}}" />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Variables: {'{{brand}}'}, {'{{model}}'}, {'{{index}}'}, {'{{location}}'}, {'{{floor}}'}
          </div>
        </div>
        <div>
          <label className="v3-field-label" style={{ fontSize: 12 }}>Site Prefix (optional)</label>
          <input value={prefix} onChange={e => setPrefix(e.target.value)} className="v3-input" placeholder="e.g. HQ" />
        </div>
        <div style={{ padding: '10px 12px', background: 'var(--bg-overlay)', borderRadius: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 4 }}>Preview</div>
          <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--text)', fontWeight: 600 }}>
            {prefix ? `${prefix}-` : ''}{preview}
          </div>
        </div>
        {msg && <p style={{ fontSize: 12, color: 'var(--success)' }}>{msg}</p>}
        <Button
          loading={saving}
          onClick={async () => {
            setSaving(true)
            try {
              await api.patch('/branding/label-settings', { label_format: format, label_prefix: prefix })
              setMsg('Label settings saved.')
            } catch { setMsg('Failed to save.') }
            finally { setSaving(false) }
          }}
        >
          Save
        </Button>
      </div>
    </GlowCard>
  )
}

function DataExportSection() {
  const { toast } = useToast()
  const [exporting, setExporting] = useState<string | null>(null)

  async function handleExport(type: string, label: string) {
    setExporting(type)
    try {
      const token = useAuthStore.getState().token
      const res = await fetch(`/api/export/${type}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${type}-export.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch {
      toast(`Failed to export ${label}`, 'error')
    } finally {
      setExporting(null)
    }
  }

  const exports = [
    { type: 'clients', label: 'Clients', desc: 'All clients and contact information' },
    { type: 'sites', label: 'Sites', desc: 'All sites with addresses and status' },
    { type: 'devices', label: 'Devices', desc: 'Complete device inventory across all sites' },
    { type: 'proposals', label: 'Proposals', desc: 'All proposals with line items and totals' },
  ]

  return (
    <GlowCard style={{ padding: 20 }} delay={0}>
      <h3 style={cardTitleStyle}>Data Export</h3>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
        Download your data as CSV files.
      </p>
      <div className="space-y-2">
        {exports.map(exp => (
          <div key={exp.type} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px', background: 'var(--bg-overlay)', borderRadius: 6,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{exp.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{exp.desc}</div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => handleExport(exp.type, exp.label)} loading={exporting === exp.type}>Download CSV</Button>
          </div>
        ))}
      </div>
    </GlowCard>
  )
}

function UniFiIntegrationSection() {
  const [status, setStatus] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [authMethod, setAuthMethod] = useState<'api_key' | 'password'>('api_key')
  const [form, setForm] = useState({ cloud_url: UNIFI_DEFAULT_CLOUD_URL, username: '', password: '', api_key: '', label: '' })
  const [showApiKey, setShowApiKey] = useState(false)
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [msg, setMsg] = useState('')
  const [showApiKeyHelp, setShowApiKeyHelp] = useState(false)
  const [testingConnectionId, setTestingConnectionId] = useState<string | null>(null)
  const [connectionTestResults, setConnectionTestResults] = useState<Record<string, { success: boolean; message?: string; error?: string }>>({})
  const { toast } = useToast()

  const reload = () => api.get('/integrations/unifi').then((r: any) => setStatus(r)).catch(() => {})

  useEffect(() => {
    reload().finally(() => setLoading(false))
  }, [])

  function getPayload() {
    const base: Record<string, string> = { cloud_url: form.cloud_url }
    if (form.label) base.label = form.label
    if (authMethod === 'api_key') {
      base.api_key = form.api_key
    } else {
      base.username = form.username
      base.password = form.password
    }
    return base
  }

  const canSubmit = authMethod === 'api_key' ? !!form.api_key : (!!form.username && !!form.password)

  async function handleTest() {
    setTesting(true)
    setTestResult(null)
    setMsg('')
    try {
      const r = await api.post('/integrations/unifi/test', getPayload()) as any
      setTestResult(r)
      if (!r.success) setMsg(r.error || 'Test failed')
    } catch (err: any) { setMsg(err?.error || err?.message || 'Connection failed — network error') }
    finally { setTesting(false) }
  }

  async function handleSave() {
    setSaving(true)
    setMsg('')
    try {
      await api.post('/integrations/unifi/save', getPayload())
      setMsg('Connection saved!')
      await reload()
      setForm({ cloud_url: UNIFI_DEFAULT_CLOUD_URL, username: '', password: '', api_key: '', label: '' })
      setTestResult(null)
      setShowAddForm(false)
    } catch { setMsg('Failed to save') }
    finally { setSaving(false) }
  }

  async function handleDisconnect(connId: string, label: string) {
    if (!confirm(`Remove connection "${label}"?`)) return
    try {
      await api.delete(`/integrations/unifi/${connId}`)
      await reload()
      setMsg('Disconnected.')
    } catch { setMsg('Failed to disconnect') }
  }

  async function handleTestSaved(conn: any) {
    setTestingConnectionId(conn.id)
    setConnectionTestResults(prev => {
      const next = { ...prev }
      delete next[conn.id]
      return next
    })
    try {
      const payload: Record<string, string> = { cloud_url: conn.cloud_url }
      if (conn.auth_method === 'api_key') {
        payload.api_key = conn.api_key
      } else {
        payload.username = conn.username
        payload.password = conn.password
      }
      const r = await api.post('/integrations/unifi/test', payload) as any
      setConnectionTestResults(prev => ({ ...prev, [conn.id]: r }))
      if (r.success) {
        toast(r.message || 'Connection successful', 'success')
      } else {
        toast(r.error || 'Connection test failed', 'error')
      }
    } catch (err: any) {
      const errMsg = err?.error || err?.message || 'Connection failed — network error'
      setConnectionTestResults(prev => ({ ...prev, [conn.id]: { success: false, error: errMsg } }))
      toast(errMsg, 'error')
    } finally {
      setTestingConnectionId(null)
    }
  }

  if (loading) return <GlowCard style={{ padding: 20 }} delay={0}><p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p></GlowCard>

  const connections: any[] = status?.connections || []
  const hasConnections = connections.length > 0

  // Connection form (shared between initial setup and "add another")
  const connectionForm = (
    <div style={!hasConnections ? cardStyle : { marginTop: 12 }}>
      {!hasConnections && <h3 style={cardTitleStyle}>UniFi Integration</h3>}
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
        {hasConnections ? 'Add another UniFi connection (cloud or self-hosted server).' : 'Connect your UniFi Cloud account or self-hosted server to sync devices.'}
      </p>
      <div className="space-y-3">
        <div>
          <label className="v3-field-label" style={{ fontSize: 12 }}>Label</label>
          <input
            value={form.label}
            onChange={e => setForm({ ...form, label: e.target.value })}
            className="v3-input"
            placeholder={form.cloud_url === UNIFI_DEFAULT_CLOUD_URL ? 'UniFi Cloud' : 'e.g., Office Server'}
          />
        </div>
        <div>
          <label className="v3-field-label" style={{ fontSize: 12 }}>URL</label>
          <input value={form.cloud_url} onChange={e => setForm({ ...form, cloud_url: e.target.value })} className="v3-input" />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, lineHeight: 1.5 }}>
            For UniFi Cloud: https://unifi.ui.com &mdash; For self-hosted: enter your controller URL.
          </div>
        </div>

        {/* Auth method toggle */}
        <div>
          <label className="v3-field-label" style={{ fontSize: 12, marginBottom: 6 }}>Authentication Method</label>
          <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <button
              type="button"
              onClick={() => setAuthMethod('api_key')}
              style={{
                flex: 1, padding: '7px 12px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: authMethod === 'api_key' ? 'var(--primary)' : 'var(--bg-overlay)',
                color: authMethod === 'api_key' ? '#fff' : 'var(--text-secondary)',
              }}
            >API Key (recommended)</button>
            <button
              type="button"
              onClick={() => setAuthMethod('password')}
              style={{
                flex: 1, padding: '7px 12px', border: 'none', borderLeft: '1px solid var(--border)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: authMethod === 'password' ? 'var(--primary)' : 'var(--bg-overlay)',
                color: authMethod === 'password' ? '#fff' : 'var(--text-secondary)',
              }}
            >Username &amp; Password</button>
          </div>
        </div>

        {/* Collapsible API key help */}
        {authMethod === 'api_key' && (
          <div style={{
            borderLeft: '3px solid var(--accent)',
            background: 'var(--bg-2)',
            borderRadius: 8,
            overflow: 'hidden',
          }}>
            <button
              type="button"
              onClick={() => setShowApiKeyHelp(!showApiKeyHelp)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--text-2)', fontSize: 12, fontWeight: 600, textAlign: 'left',
              }}
            >
              <span>How to get your API key</span>
              <span style={{ fontSize: 10, color: 'var(--text-2)', transform: showApiKeyHelp ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>&#9660;</span>
            </button>
            {showApiKeyHelp && (
              <div style={{ padding: '0 14px 12px 14px', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.7 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Go to <strong style={{ color: 'var(--text)' }}>unifi.ui.com</strong> (NOT your console's IP address)</li>
                  <li>Log into your Ubiquiti SSO account</li>
                  <li>Click your profile icon, then <strong style={{ color: 'var(--text)' }}>API Keys</strong> (or navigate to Account Settings &rarr; API Keys)</li>
                  <li>Create a new API key, copy it, and paste it here</li>
                </ol>
                <div style={{
                  marginTop: 10, padding: '8px 10px',
                  background: 'rgba(217, 119, 6, 0.08)', border: '1px solid rgba(217, 119, 6, 0.2)', borderRadius: 6,
                }}>
                  <p style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 500, margin: 0 }}>
                    API keys created from the console admin panel (192.168.x.x) will NOT work. You must create the key from unifi.ui.com.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {authMethod === 'api_key' ? (
          <div>
            <label className="v3-field-label" style={{ fontSize: 12 }}>API Key</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showApiKey ? 'text' : 'password'}
                value={form.api_key}
                onChange={e => setForm({ ...form, api_key: e.target.value })}
                className="v3-input"
                placeholder="Enter your UniFi API key"
                style={{ paddingRight: 60 }}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', fontSize: 11, color: 'var(--text-tertiary)',
                  cursor: 'pointer', fontWeight: 600,
                }}
              >{showApiKey ? 'Hide' : 'Show'}</button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              Cloud: generate at unifi.ui.com &rarr; Settings &rarr; API.<br />
              Self-hosted: generate at Network &rarr; Control Plane &rarr; Integrations.
            </p>
          </div>
        ) : (
          <>
            <div>
              <label className="v3-field-label" style={{ fontSize: 12 }}>Username</label>
              <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className="v3-input" />
            </div>
            <div>
              <label className="v3-field-label" style={{ fontSize: 12 }}>Password</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="v3-input" />
            </div>
            <div style={{ padding: '8px 10px', background: 'rgba(217, 119, 6, 0.08)', border: '1px solid rgba(217, 119, 6, 0.2)', borderRadius: 6 }}>
              <p style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 500, margin: 0 }}>
                Does not work with 2FA enabled. Use API Key instead.
              </p>
            </div>
          </>
        )}

        {testResult?.success && (
          <div style={{ background: 'var(--bg-overlay)', borderRadius: 6, padding: 10, marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginBottom: 4 }}>Connection successful — {testResult.sites?.length || 0} sites found</div>
            {testResult.sites?.map((s: any) => (
              <div key={s.id} style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '2px 0' }}>
                {s.desc || s.id} ({s.device_count} devices)
              </div>
            ))}
          </div>
        )}
        {msg && <p style={{ fontSize: 12, color: msg.includes('saved') ? 'var(--success)' : 'var(--danger)' }}>{msg}</p>}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button variant="secondary" onClick={handleTest} disabled={testing || !canSubmit} size="sm">
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button onClick={handleSave} disabled={!canSubmit} loading={saving} size="sm">
            Save Connection
          </Button>
          {hasConnections && (
            <Button variant="secondary" size="sm" onClick={() => { setShowAddForm(false); setTestResult(null); setMsg('') }}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  )

  if (!hasConnections) return connectionForm

  return (
    <div>
      <GlowCard style={{ padding: 20 }} delay={0}>
        <h3 style={cardTitleStyle}>UniFi Integration</h3>

        {/* List all connections */}
        {connections.map((conn: any) => {
          const authLabel = conn.auth_method === 'api_key' ? 'API Key' : 'Username/Password'
          return (
            <div key={conn.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {conn.label || conn.cloud_url}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2, paddingLeft: 16 }}>
                {conn.cloud_url} &middot; {authLabel}
                {conn.username && <span> ({conn.username})</span>}
              </div>
              {conn.last_sync_at && (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', paddingLeft: 16 }}>
                  Last sync: {new Date(conn.last_sync_at).toLocaleString()}
                </div>
              )}
              {connectionTestResults[conn.id] && (
                <div style={{
                  fontSize: 11, paddingLeft: 16, marginTop: 4,
                  color: connectionTestResults[conn.id].success ? 'var(--success)' : 'var(--danger)',
                }}>
                  {connectionTestResults[conn.id].success
                    ? (connectionTestResults[conn.id].message || 'Connection successful')
                    : (connectionTestResults[conn.id].error || 'Test failed')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6, paddingLeft: 16 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleTestSaved(conn)}
                  loading={testingConnectionId === conn.id}
                >
                  Test Connection
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDisconnect(conn.id, conn.label || conn.cloud_url)}
                  style={{ color: 'var(--danger)' }}
                >
                  Remove
                </Button>
              </div>
            </div>
          )
        })}

        {msg && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>{msg}</p>}

        {!showAddForm && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setShowAddForm(true); setMsg('') }}
            style={{ marginTop: 12 }}
          >
            + Add Connection
          </Button>
        )}
      </GlowCard>

      {showAddForm && connectionForm}

      {/* Site Mapping staging queue — uses first connection for backward compat */}
      <UniFiStagingQueue connectionId={connections[0]?.id} />
    </div>
  )
}

function DeviceIconsSection() {
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [clearing, setClearing] = useState(false)
  const [msg, setMsg] = useState('')
  const [unknownModels, setUnknownModels] = useState<any[]>([])
  const [addingModel, setAddingModel] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState('')
  const [saving, setSaving] = useState(false)

  const loadStats = () => {
    api.get('/admin/device-icon-stats')
      .then((r: any) => setStats(r))
      .catch(() => setStats(null))
      .finally(() => setLoading(false))
  }

  const loadUnknownModels = () => {
    api.get('/admin/unknown-device-models')
      .then((r: any) => setUnknownModels(Array.isArray(r) ? r : []))
      .catch(() => setUnknownModels([]))
  }

  useEffect(() => { loadStats(); loadUnknownModels() }, [])

  async function handleClear() {
    if (!confirm('Clear all cached device icons? They will re-download on next UniFi sync.')) return
    setClearing(true)
    setMsg('')
    try {
      const r = await api.post('/admin/clear-device-icons') as any
      setMsg(r.message || 'Icons cleared.')
      loadStats()
    } catch { setMsg('Failed to clear icons.') }
    finally { setClearing(false) }
  }

  async function handleAddImage(model: string) {
    if (!imageUrl.trim()) return
    setSaving(true)
    setMsg('')
    try {
      await api.post('/admin/device-image-registry', { model, source_url: imageUrl.trim() })
      setMsg(`Image saved for ${model}. It will appear on next sync.`)
      setAddingModel(null)
      setImageUrl('')
      loadUnknownModels()
      loadStats()
    } catch (err: any) {
      setMsg(`Failed to save image: ${err?.message || 'unknown error'}`)
    } finally { setSaving(false) }
  }

  return (
    <GlowCard style={{ padding: 20 }} delay={0}>
      <h3 style={cardTitleStyle}>Device Icons</h3>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
        Icons are fetched from Ubiquiti&apos;s CDN on first sync and cached locally in R2.
      </p>

      {loading ? (
        <div className="skeleton" style={{ height: 60, borderRadius: 6 }} />
      ) : (
        <div style={{ padding: '12px 14px', background: 'var(--bg-overlay)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
            {stats?.count ?? 0} device icons cached
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
            Total size: {stats?.total_display ?? '0 KB'}
          </div>
        </div>
      )}

      {unknownModels.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--warning)', marginBottom: 8 }}>
            {unknownModels.length} UniFi model{unknownModels.length !== 1 ? 's' : ''} missing product images
          </div>
          <table className="v3-table" style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)' }}>Model</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)' }}>Type</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)' }}>First Seen</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)' }}>Seen</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {unknownModels.map((m: any) => (
                <tr key={m.model} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{m.model}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{m.unifi_type || '—'}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-tertiary)' }}>
                    {m.first_seen_at ? new Date(m.first_seen_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{m.seen_count}x</td>
                  <td style={{ padding: '6px 8px' }}>
                    {addingModel === m.model ? (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input
                          type="text"
                          value={imageUrl}
                          onChange={e => setImageUrl(e.target.value)}
                          placeholder="Paste image URL..."
                          style={{ flex: 1, fontSize: 11, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)', minWidth: 160 }}
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') handleAddImage(m.model); if (e.key === 'Escape') { setAddingModel(null); setImageUrl('') } }}
                        />
                        <Button size="sm" onClick={() => handleAddImage(m.model)} disabled={saving || !imageUrl.trim()}>
                          {saving ? '...' : 'Save'}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => { setAddingModel(null); setImageUrl('') }}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => { setAddingModel(m.model); setImageUrl('') }}>
                        Add Image URL
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {addingModel && (
            <p style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 6 }}>
              To find the image URL: open your UniFi controller, find the device, right-click the device image, Inspect Element, and copy the img src URL.
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleClear}
          disabled={clearing}
          loading={clearing}
        >
          {clearing ? 'Clearing...' : 'Clear Icon Cache'}
        </Button>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 12 }}>
        Icons will automatically re-download on the next UniFi device sync.
      </p>

      {msg && <p style={{ fontSize: 12, color: msg.includes('Failed') ? 'var(--danger)' : 'var(--success)', marginTop: 8 }}>{msg}</p>}
    </GlowCard>
  )
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-xs)',
  padding: 20,
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 16,
}

const CSS = `
  .settings-nav {
    width: 160px;
    flex-shrink: 0;
    border-right: 1px solid var(--border-subtle);
    padding-right: 12px;
    position: sticky;
    top: 0;
    align-self: flex-start;
    max-height: calc(100vh - 52px);
    overflow-y: auto;
  }
  .settings-nav-group {
    font-size: 9px;
    font-weight: 800;
    color: var(--text-disabled);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 6px 10px;
    margin-top: 8px;
  }
  .settings-nav-item {
    display: block;
    width: 100%;
    padding: 6px 10px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--text-tertiary);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: all 0.1s;
    font-family: var(--font-sans);
  }
  .settings-nav-item:hover {
    background: var(--bg-overlay);
    color: var(--text-secondary);
  }
  .settings-nav-item.active {
    background: var(--accent-muted);
    color: var(--accent);
    font-weight: 600;
  }
  .page-enter {
    animation: pageIn 0.35s cubic-bezier(0.16,1,0.3,1);
  }
  @keyframes pageIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .settings-mobile-nav { display: none; margin-bottom: 16px; }
  @media (max-width: 768px) {
    .settings-mobile-nav { display: block; }
    .settings-nav {
      display: none;
    }
  }
`

function QuickBooksSection() {
  const [connection, setConnection] = useState<{ status: string; company_name: string | null; last_sync_at: string | null } | null>(null)
  const [loading, setLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState(false)
  const { toast, toastError } = useToast()

  useEffect(() => {
    api.get<{ status: string; company_name: string | null; last_sync_at: string | null }>('/quickbooks/connection')
      .then(setConnection)
      .catch(() => setConnection({ status: 'disconnected', company_name: null, last_sync_at: null }))
      .finally(() => setLoading(false))
  }, [])

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      await api.post('/quickbooks/disconnect')
      setConnection({ status: 'disconnected', company_name: null, last_sync_at: null })
      toast('QuickBooks disconnected')
    } catch (err) {
      toastError('Failed to disconnect', err)
    } finally {
      setDisconnecting(false)
    }
  }

  const isConnected = connection?.status === 'connected'

  return (
    <GlowCard style={{ padding: 20 }} delay={0}>
      <h3 style={cardTitleStyle}>QuickBooks Integration</h3>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)', padding: '12px 0' }}>Loading...</div>
      ) : isConnected ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)', display: 'inline-block' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Connected</span>
          </div>
          {connection?.company_name && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
              Company: <strong style={{ color: 'var(--text)' }}>{connection.company_name}</strong>
            </div>
          )}
          {connection?.last_sync_at && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
              Last sync: {new Date(connection.last_sync_at).toLocaleString()}
            </div>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
            loading={disconnecting}
            style={{ color: 'var(--danger)' }}
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect QuickBooks'}
          </Button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--text-3)', display: 'inline-block' }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>Not Connected</span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            Connect QuickBooks to sync invoices, customers, and payments.
          </p>
          <Button
            onClick={() => {
              toast('QuickBooks OAuth setup requires client credentials. Contact support to configure.')
            }}
          >
            Connect QuickBooks
          </Button>
        </div>
      )}
    </GlowCard>
  )
}

function AIConfigSection() {
  const [config, setConfig] = useState<any>(null)
  const [starters, setStarters] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [usage, setUsage] = useState<any>(null)
  const { toast, toastError } = useToast()

  useEffect(() => {
    Promise.all([
      api.get('/ai-config').then(r => setConfig(r)).catch(() => {}),
      api.get('/ai-config/starters').then((r: any) => setStarters(r)).catch(() => {}),
    ]).finally(() => setLoading(false))
    // Load usage stats (non-blocking)
    api.get('/chat/admin/stats').then((r: any) => setUsage(r)).catch(() => {})
  }, [])

  const save = async () => {
    if (!config) return
    setSaving(true)
    try {
      await api.put('/ai-config', config)
      toast('AI configuration saved')
    } catch (err) {
      toastError('Failed to save AI config', err)
    } finally {
      setSaving(false)
    }
  }

  const addStarter = async () => {
    if (starters.length >= 6) { toast('Maximum 6 starters allowed', 'error'); return }
    try {
      const res = await api.post('/ai-config/starters', { label: 'New Starter', icon: '💬', sort_order: starters.length }) as any
      setStarters(prev => [...prev, res])
    } catch (err) {
      toastError('Failed to add starter', err)
    }
  }

  const updateStarter = async (id: string, field: string, value: string) => {
    try {
      await api.patch(`/ai-config/starters/${id}`, { [field]: value })
      setStarters(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s))
    } catch (err) {
      toastError('Failed to update starter', err)
    }
  }

  const deleteStarter = async (id: string) => {
    try {
      await api.delete(`/ai-config/starters/${id}`)
      setStarters(prev => prev.filter(s => s.id !== id))
      toast('Starter deleted')
    } catch (err) {
      toastError('Failed to delete starter', err)
    }
  }

  if (loading) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>Loading...</div>
  if (!config) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>AI Configuration unavailable</div>

  const update = (field: string, value: any) => setConfig((c: any) => ({ ...c, [field]: value }))

  const aiName = config.ai_name || 'AI Assistant'
  const toneGreetings: Record<string, string> = {
    professional: `Hello. I'm ${aiName}. How may I assist you today?`,
    friendly: `Hi there! I'm ${aiName}. How can I help you with your systems today?`,
    casual: `Hey! I'm ${aiName}. What's going on?`,
    technical: `${aiName} online. Please describe the issue or system you need help with.`,
  }

  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 20,
  }
  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-2)',
    textTransform: 'uppercase' as const,
    letterSpacing: '.04em',
    marginBottom: 12,
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }

  return (
    <div style={{ animation: 'fadeInUp 0.25s ease-out', maxWidth: 640 }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>AI Configuration</h2>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
        Customize how your AI assistant communicates with clients.
      </p>

      {/* ── PERSONALITY ──────────────────────────── */}
      <GlowCard style={{ padding: '20px 24px', marginBottom: 20 }} delay={0}>
        <div style={sectionTitleStyle}>Personality</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Assistant Name</label>
            <input className="v3-input" value={config.ai_name || ''} onChange={e => update('ai_name', e.target.value)} placeholder="e.g. Amber Assistant" style={{ width: '100%' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Tone</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {['professional', 'friendly', 'casual', 'technical'].map(t => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name="tone" value={t} checked={config.tone === t} onChange={() => update('tone', t)} style={{ accentColor: 'var(--accent-h)' }} />
                  <span style={{ textTransform: 'capitalize' }}>{t}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div style={{
          background: 'rgba(22, 22, 34, 0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          padding: 16,
          position: 'relative',
        }}>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Live Preview</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9999,
              background: 'linear-gradient(135deg, var(--accent-h), var(--accent))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 14, color: 'var(--accent-t)',
            }}>
              {aiName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{aiName}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Online</div>
            </div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '12px 12px 12px 4px',
            padding: '10px 14px',
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--text-primary)',
            marginBottom: 12,
          }}>
            {toneGreetings[config.tone || 'friendly']}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {starters.slice(0, 3).map(s => (
              <div key={s.id} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, padding: '6px 10px',
                fontSize: 12, color: 'var(--text-primary)',
                display: 'flex', gap: 6, alignItems: 'center',
              }}>
                <span>{s.icon}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </GlowCard>

      {/* ── CUSTOM INSTRUCTIONS ──────────────────── */}
      <GlowCard style={{ padding: '20px 24px', marginBottom: 20 }} delay={50}>
        <div style={sectionTitleStyle}>Custom Instructions</div>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
          Tell the AI how to represent your company. These instructions are included in every conversation.
        </p>
        <Textarea
          value={config.custom_instructions || ''}
          onChange={e => update('custom_instructions', e.target.value)}
          placeholder={"- Always recommend our Guardian Shield plan\n- Business hours: Mon-Fri 8am-5pm EST\n- For pricing: \"I'll have sales reach out\"\n- Emergency line: 555-911-SAFE"}
          style={{ width: '100%', minHeight: 120, resize: 'vertical', fontSize: 13, lineHeight: 1.6 }}
        />
      </GlowCard>

      {/* ── SCOPE ────────────────────────────────── */}
      <GlowCard style={{ padding: '20px 24px', marginBottom: 20 }} delay={100}>
        <div style={sectionTitleStyle}>Scope</div>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
          What topics can the AI discuss?
        </p>
        {[
          { field: 'scope_general_it', label: 'General IT questions', desc: 'Networking, Wi-Fi, basic computer help' },
          { field: 'scope_industry', label: 'Industry knowledge (NFPA, NEC, codes)', desc: 'Security standards and compliance' },
          { field: 'scope_product_recs', label: 'Product recommendations', desc: 'Suggest cameras, access control, etc.' },
          { field: 'scope_pricing', label: 'Pricing discussions', desc: 'Discuss costs and quotes' },
        ].map(s => (
          <Checkbox
            key={s.field}
            label={s.label}
            description={s.desc}
            checked={!!config[s.field]}
            onChange={e => update(s.field, (e.target as HTMLInputElement).checked ? 1 : 0)}
          />
        ))}

        <div style={{ marginTop: 16 }}>
          <div className="v3-field-label" style={{ marginBottom: 8 }}>Blocked</div>
          <Checkbox
            label="Never recommend competitors"
            checked={!!config.block_competitors}
            onChange={e => update('block_competitors', (e.target as HTMLInputElement).checked ? 1 : 0)}
          />
          <Checkbox
            label="Never discuss pricing"
            checked={!!config.block_pricing}
            onChange={e => update('block_pricing', (e.target as HTMLInputElement).checked ? 1 : 0)}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Off-topic response</label>
          <Textarea
            value={config.off_topic_response || ''}
            onChange={e => update('off_topic_response', e.target.value)}
            placeholder="I'm here to help with your security systems. How can I assist?"
            style={{ width: '100%', minHeight: 50, resize: 'vertical', fontSize: 13 }}
          />
        </div>
      </GlowCard>

      {/* ── CONVERSATION STARTERS ────────────────── */}
      <GlowCard style={{ padding: '20px 24px', marginBottom: 20 }} delay={150}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={sectionTitleStyle}>Conversation Starters</div>
          <button onClick={addStarter} disabled={starters.length >= 6} style={{
            fontSize: 12, padding: '5px 12px', background: 'var(--primary)', color: '#fff',
            border: 'none', borderRadius: 6, cursor: starters.length >= 6 ? 'not-allowed' : 'pointer',
            fontWeight: 600, opacity: starters.length >= 6 ? 0.5 : 1,
          }}>+ Add Starter</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
          Shown when clients open the chat widget. Max 6. Drag to reorder.
        </p>
        {starters.map(s => (
          <div key={s.id} style={{
            display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center',
            padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 8,
            border: '1px solid var(--border-subtle)',
          }}>
            <span style={{ cursor: 'grab', color: 'var(--text-tertiary)', fontSize: 14 }}>&#9776;</span>
            <input className="v3-input" value={s.icon || ''} onChange={e => updateStarter(s.id, 'icon', e.target.value)} style={{ width: 40, textAlign: 'center', fontSize: 16, padding: '4px' }} />
            <input className="v3-input" value={s.label || ''} onChange={e => updateStarter(s.id, 'label', e.target.value)} placeholder="Label" style={{ flex: 1, fontSize: 13 }} />
            <input className="v3-input" value={s.opening_message || ''} onChange={e => updateStarter(s.id, 'opening_message', e.target.value)} placeholder="Opening message..." style={{ flex: 2, fontSize: 13 }} />
            <button onClick={() => deleteStarter(s.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14, padding: '4px 6px' }}>&#10005;</button>
          </div>
        ))}
        {starters.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            No starters yet. Add one to get started.
          </div>
        )}
      </GlowCard>

      {/* ── ESCALATION RULES ────────────────────── */}
      <GlowCard style={{ padding: '20px 24px', marginBottom: 20 }} delay={200}>
        <div style={sectionTitleStyle}>Escalation Rules</div>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
          Auto-escalate to human when:
        </p>
        <Checkbox
          label="Client mentions emergency keywords"
          checked={!!config.escalate_emergency}
          onChange={e => update('escalate_emergency', (e.target as HTMLInputElement).checked ? 1 : 0)}
        />
        <Checkbox
          label="AI confidence is below threshold"
          checked={!!config.escalate_low_confidence}
          onChange={e => update('escalate_low_confidence', (e.target as HTMLInputElement).checked ? 1 : 0)}
        />
        <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Confidence Threshold (%)</label>
            <input type="number" className="v3-input" value={config.confidence_threshold || 70} onChange={e => update('confidence_threshold', parseInt(e.target.value))} min={10} max={100} step={5} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Max Messages</label>
            <input type="number" className="v3-input" value={config.max_messages_before_escalate || 10} onChange={e => update('max_messages_before_escalate', parseInt(e.target.value))} min={1} max={50} style={{ width: '100%' }} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Custom Trigger Keywords (JSON array)</label>
          <input className="v3-input" value={config.custom_escalation_triggers || ''} onChange={e => update('custom_escalation_triggers', e.target.value)} placeholder='["alarm going off", "fire", "flood"]' style={{ width: '100%', fontSize: 13 }} />
        </div>
      </GlowCard>

      {/* ── USAGE THIS MONTH ─────────────────────── */}
      {usage && (
        <GlowCard style={{ padding: '20px 24px', marginBottom: 20 }} delay={250}>
          <div style={sectionTitleStyle}>Usage This Month</div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {usage.active_conversations}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Active Chats</div>
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {usage.resolved_today}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Resolved Today</div>
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {usage.ai_resolution_rate}%
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>AI Resolution</div>
            </div>
          </div>
        </GlowCard>
      )}

      <Button onClick={save} loading={saving} style={{ marginTop: 16 }}>
        Save
      </Button>
    </div>
  )
}
